import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { DEFAULT_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { rem } from 'polished'
import type { MouseEventHandler } from 'react'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ToggleButton from 'LuxKit/ToggleButton'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'

const SelectionContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-column-gap: ${rem(12)};
  flex-wrap: wrap;
  gap: ${rem(12)};
  align-items: center;
  justify-content: center;
  width: fit-content;
  margin: auto;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(4, 1fr);
  }
`

interface Props {
  durationOptionsCustom?: Array<DurationOption>
  previouslySelectedDuration?: FLEXIBLE_DURATION_RANGE
  onDurationChange?: (duration: string) => void
  className?: string
}

export interface DurationOption {
  title: string
  caption: string
  id: FLEXIBLE_DURATION_RANGE
}

export function getDurationOptionsDefault(intl: IntlShape): Array<DurationOption> {
  return [
    {
      title: intl.formatMessage({
        id: '7EMZX4qO66',
        defaultMessage: 'Any duration',
        description: 'Duration picker option title for any duration stay',
      }),
      caption: intl.formatMessage({
        id: 'Fn0aJv5lov',
        defaultMessage: 'I’ll decide later',
        description: 'Duration picker caption for the any duration option',
      }),
      id: DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION,
    },
    {
      title: intl.formatMessage({
        id: 'hOLmgQUiT7',
        defaultMessage: 'Short stay',
        description: 'Duration picker option title for a short stay (1-3 nights)',
      }),
      caption: intl.formatMessage({
        id: 'Ac+Ziq7Kbh',
        defaultMessage: '1 to 3 nights',
        description: 'Duration picker caption describing the short stay range',
      }),
      id: DEFAULT_FLEXIBLE_DURATION_RANGE.SHORT_STAY,
    },
    {
      title: intl.formatMessage({
        id: 'TnEm6A0q48',
        defaultMessage: 'Medium stay',
        description: 'Duration picker option title for a medium stay (4-7 nights)',
      }),
      caption: intl.formatMessage({
        id: 'qtfTfpJ4TB',
        defaultMessage: '4 to 7 nights',
        description: 'Duration picker caption describing the medium stay range',
      }),
      id: DEFAULT_FLEXIBLE_DURATION_RANGE.MEDIUM_STAY,
    },
    {
      title: intl.formatMessage({
        id: 'igwdb/+8ic',
        defaultMessage: 'Long stay',
        description: 'Duration picker option title for a long stay (8+ nights)',
      }),
      caption: intl.formatMessage({
        id: 'i0zU8agerM',
        defaultMessage: '8+ nights',
        description: 'Duration picker caption describing the long stay range',
      }),
      id: DEFAULT_FLEXIBLE_DURATION_RANGE.LONG_STAY,
    },
  ]
}

/** @deprecated Use getDurationOptionsDefault(intl) instead */
export const durationOptionsDefault: Array<DurationOption> = [
  {
    title: 'Any duration',
    caption: 'I’ll decide later',
    id: DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION,
  },
  {
    title: 'Short stay',
    caption: '1 to 3 nights',
    id: DEFAULT_FLEXIBLE_DURATION_RANGE.SHORT_STAY,
  },
  {
    title: 'Medium stay',
    caption: '4 to 7 nights',
    id: DEFAULT_FLEXIBLE_DURATION_RANGE.MEDIUM_STAY,
  },
  {
    title: 'Long stay',
    caption: '8+ nights',
    id: DEFAULT_FLEXIBLE_DURATION_RANGE.LONG_STAY,
  },
]

function DurationPicker(props: Props) {
  const intl = useIntl()
  const durationOptionsIntl = useMemo(() => getDurationOptionsDefault(intl), [intl])
  const { durationOptionsCustom = durationOptionsIntl, onDurationChange, previouslySelectedDuration } = props
  const [flexibleNights, setFlexibleNights] =
    useState<FLEXIBLE_DURATION_RANGE>(
      previouslySelectedDuration ?? DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION,
    )

  const onDurationClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      const duration = event.currentTarget.dataset.option as FLEXIBLE_DURATION_RANGE
      const newFlexibleDuration = duration === flexibleNights ?
        DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION :
        duration
      setFlexibleNights(newFlexibleDuration)
      onDurationChange?.(newFlexibleDuration)
    },
    [flexibleNights, onDurationChange],
  )

  return (
    <SelectionContainer data-testid="flexibleDurationPicker" className={props.className}>
      {durationOptionsCustom.map((option) => <ToggleButton
        key={option.id}
        kind="secondary"
        title={option.title}
        subtitle={option.caption}
        isSelected={option.id === flexibleNights}
        data-testid={`duration-option-${option.id}`}
        data-option={option.id}
        onClick={onDurationClick}
      />)}
    </SelectionContainer>
  )
}

export default DurationPicker
